import { getMessaging, getToken, onMessage, Messaging } from 'firebase/messaging';
import { db } from './firebaseConfig';
import { doc, updateDoc, getDoc, setDoc, arrayUnion } from 'firebase/firestore';

let messaging: Messaging | null = null;

export const initFCM = async (userId: string): Promise<string | null> => {
  try {
    // Check if browser supports service workers
    if (!('serviceWorker' in navigator)) {
      console.log('Service Workers not supported');
      return null;
    }

    // Check if Firebase Messaging is supported
    try {
      messaging = getMessaging();
    } catch (error) {
      console.log('Firebase Messaging not available:', error);
      return null;
    }

    // Check for existing token in Firestore
    const memberDoc = await getDoc(doc(db, 'members', userId));
    if (memberDoc.exists() && memberDoc.data()?.fcmToken) {
      console.log('Using existing FCM token');
      return memberDoc.data().fcmToken;
    }

    // Request permission
    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      try {
        const vapidKey = process.env.REACT_APP_FCM_VAPID_KEY;
        if (!vapidKey) {
          console.warn('FCM VAPID key not configured');
          return null;
        }

        const token = await getToken(messaging, {
          vapidKey: vapidKey,
        });

        if (token) {
          console.log('FCM Token obtained:', token.substring(0, 20) + '...');
          
          // Save token to Firestore
          await saveFCMToken(userId, token);
          
          return token;
        }
      } catch (error) {
        console.error('Error getting FCM token:', error);
      }
    } else if (permission === 'denied') {
      console.log('Notification permission denied by user');
    }

    return null;
  } catch (error) {
    console.error('FCM initialization failed:', error);
    return null;
  }
};

export const saveFCMToken = async (userId: string, token: string): Promise<boolean> => {
  try {
    const memberRef = doc(db, 'members', userId);
    const memberDoc = await getDoc(memberRef);

    if (memberDoc.exists()) {
      await updateDoc(memberRef, {
        fcmToken: token,
        fcmTokenUpdatedAt: new Date().toISOString(),
        fcmTokens: arrayUnion(token), // Keep history of tokens
      });
    } else {
      await setDoc(memberRef, {
        fcmToken: token,
        fcmTokenUpdatedAt: new Date().toISOString(),
        fcmTokens: [token],
      }, { merge: true });
    }

    console.log('FCM token saved to Firestore');
    return true;
  } catch (error) {
    console.error('Error saving FCM token:', error);
    return false;
  }
};

export const removeFCMToken = async (userId: string) => {
  try {
    const memberRef = doc(db, 'members', userId);
    await updateDoc(memberRef, {
      fcmToken: null,
      fcmTokenRevokedAt: new Date().toISOString(),
    });
    console.log('FCM token removed');
  } catch (error) {
    console.error('Error removing FCM token:', error);
  }
};

export const setupFCMListeners = (userId: string, onNotification?: (data: any) => void) => {
  if (!messaging) {
    console.log('Messaging not initialized');
    return () => {};
  }

  // Listen to foreground messages
  const unsubscribe = onMessage(messaging, (payload) => {
    console.log('Message received in foreground:', payload);

    const notificationTitle = payload.notification?.title || 'Famitry';
    const notificationBody = payload.notification?.body || '';
    const notificationIcon = payload.notification?.icon || '/logo-192.png';

    // Prepare notification data
    const notificationData = {
      title: notificationTitle,
      body: notificationBody,
      icon: notificationIcon,
      badge: '/logo-192.png',
      tag: 'famitry-notification',
      requireInteraction: false,
      data: payload.data || {},
    };

    // Show notification
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(notificationTitle, {
        body: notificationBody,
        icon: notificationIcon,
        badge: '/logo-192.png',
        tag: 'famitry-notification',
        data: payload.data || {},
      });
    }

    // Call custom handler if provided
    if (onNotification) {
      onNotification(notificationData);
    }

    // Log to analytics (in-app notification)
    trackNotificationReceived(userId, payload.data?.type || 'unknown');
  });

  return unsubscribe;
};

export const registerNotificationClickHandler = (userId: string) => {
  if (!('serviceWorkerContainer' in navigator)) return;

  // Listen for notification clicks
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data.type === 'NOTIFICATION_CLICK') {
        const data = event.data.notification.data;
        handleNotificationClick(data);
      }
    });
  }
};

export const handleNotificationClick = (data: Record<string, any>) => {
  console.log('Notification clicked:', data);

  const { type, entityId, entityType } = data;

  // Navigate to relevant section based on notification type
  switch (type) {
    case 'post_liked':
    case 'post_commented':
      // Navigate to feed and highlight post
      window.location.hash = '#/feed';
      window.dispatchEvent(
        new CustomEvent('navigate', {
          detail: { tab: 'feed', highlightPost: entityId },
        })
      );
      break;

    case 'message':
      // Navigate to messages
      window.location.hash = '#/messages';
      window.dispatchEvent(
        new CustomEvent('navigate', {
          detail: { tab: 'messages', conversationId: entityId },
        })
      );
      break;

    case 'member_joined':
      // Navigate to directory or tree
      window.location.hash = '#/tree';
      window.dispatchEvent(
        new CustomEvent('navigate', {
          detail: { tab: 'tree', focusId: entityId },
        })
      );
      break;

    case 'birthday':
    case 'anniversary':
      // Navigate to directory
      window.location.hash = '#/directory';
      window.dispatchEvent(
        new CustomEvent('navigate', {
          detail: { tab: 'directory', searchTerm: entityId },
        })
      );
      break;

    case 'admin_alert':
      // Navigate to admin dashboard
      window.location.hash = '#/admin';
      window.dispatchEvent(
        new CustomEvent('navigate', {
          detail: { tab: 'admin' },
        })
      );
      break;

    default:
      // Default to feed
      window.location.hash = '#/feed';
  }

  // Close notification
  if ('serviceWorkerContainer' in navigator) {
    navigator.serviceWorker.controller?.postMessage({
      type: 'CLOSE_NOTIFICATION',
    });
  }
};

export const trackNotificationReceived = (userId: string, type: string) => {
  // Log to analytics
  try {
    const event = {
      userId,
      type,
      timestamp: new Date().toISOString(),
      platform: 'web',
    };
    console.log('Notification received event:', event);
  } catch (error) {
    console.error('Error tracking notification:', error);
  }
};

export const requestNotificationPermission = async (): Promise<boolean> => {
  try {
    if (!('Notification' in window)) {
      console.log('Notifications not supported');
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission === 'denied') {
      console.log('Notification permission previously denied');
      return false;
    }

    const permission = await Notification.requestPermission();
    return permission === 'granted';
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return false;
  }
};

export const checkNotificationPermission = (): 'granted' | 'denied' | 'default' => {
  if (!('Notification' in window)) {
    return 'denied';
  }
  return Notification.permission;
};

export const isNotificationSupported = (): boolean => {
  return (
    'Notification' in window &&
    'serviceWorker' in navigator
  );
};

export const getMessagingInstance = () => messaging;
